import { useQuery } from '@tanstack/react-query';
import {
  listRuns,
  type ProcessRunListItemDto,
} from '@api/processRuns';
import { processRunsListQueryKey } from './processRunsQueryKeys';

export interface UseProcessRunsListReturn {
  readonly runs: readonly ProcessRunListItemDto[];
  readonly totalRuns: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Fetch the first page of runs for a process. Used by the sidebar to
 * derive analytics stats and to identify the most recent completed run.
 *
 * The query key aligns with {@link processRunsListQueryKey} so
 * invalidations from {@link useProcessRunSession} (after a run
 * starts / finishes) automatically refresh this list.
 */
export function useProcessRunsList(
  processId: string | undefined,
): UseProcessRunsListReturn {
  const query = useQuery({
    queryKey: processRunsListQueryKey(processId ?? ''),
    queryFn: () => listRuns(processId!, { size: 50 }),
    enabled: Boolean(processId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    runs: query.data?.runs ?? [],
    totalRuns: query.data?.totalElements ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
